import type { StatusTitleResult } from 'src/app/Orders/order.helpers';

import { withItemsVerb } from './nba.helpers';

/**
 * Wording for a later stop page while the shipment has not started that leg yet
 * (pages 2..N of the status card). Written from the customer's side: it is "your
 * shipment", never "freight" or "this leg"; no leg ids, no carrier mechanics. Same
 * shape as every other card state: bold title, grey subtitle with the live fact, one
 * sentence below (Lucas, 2026-09-05).
 *
 * Whether legs are displayed comes from the portal visibility setting "Display Order
 * Legs": when it is off the backend has already merged internal junctions, so only the
 * customer's own stops remain and a carrier change is never mentioned.
 */
export interface AwaitingStopInput {
    legsDisplayed: boolean;
    isFinalLeg: boolean;
    /** Normalised status of the previous presented leg (the one bringing the shipment here). */
    prevStatus: string;
    prevPhysicallyDone: boolean;
    /** The stop this page is about: where this leg starts and the shipment arrives next. */
    city: string;
    /** Where the shipment goes after this stop (this leg's destination; on the last leg, the final delivery). */
    nextCity: string;
    hasPickupHere: boolean;
    hasDropHere: boolean;
    isSameCarrierRun: boolean;
    pickupItems: string | null;
    dropItems: string | null;
    finalItems: string | null;
}

type StopActivity = 'final' | 'both' | 'drop' | 'pickup' | 'transfer' | 'unknown';
type StopPhase = 'moving' | 'arrived' | 'waiting';

const cap = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

const getActivity = (input: AwaitingStopInput): StopActivity => {
    // What happens at THIS stop comes first, even on the last leg (a customer pickup at the start
    // of the final leg is still a pickup). "final" is the last leg starting at a stop with nothing
    // of the customer's routed there (a hand-off), so the only thing left to say is the delivery.
    if (input.hasPickupHere && input.hasDropHere) return 'both';
    if (input.hasDropHere) return 'drop';
    if (input.hasPickupHere) return 'pickup';
    if (input.isFinalLeg) return 'final';
    // Nothing routed here: with legs displayed a carrier change is a transfer stop; otherwise we
    // do not know what happens at this stop and say only where the shipment is going.
    if (input.legsDisplayed && !input.isSameCarrierRun) return 'transfer';

    return 'unknown';
};

const getPhase = (input: AwaitingStopInput): StopPhase => {
    if (input.prevStatus === 'in-transit') return 'moving';
    if (input.prevPhysicallyDone) return 'arrived';

    return 'waiting';
};

// eslint-disable-next-line complexity
export const buildAwaitingStopTitleParts = (input: AwaitingStopInput): StatusTitleResult => {
    const activity = getActivity(input);
    const phase = getPhase(input);
    const { city, nextCity, pickupItems, dropItems, finalItems } = input;

    const cityOr = city || 'its next stop';
    const nextOr = nextCity || 'its next stop';
    const nearing = city ? `Nearing ${city}` : 'Almost there';
    const inCity = city ? `In ${city}` : 'Arrived';
    const headingTo = city ? `Heading to ${city}` : 'Heading to the next stop';

    const pickupWhat = pickupItems || 'more of your shipment';
    const dropWhat = dropItems || 'part of your shipment';
    // Where the shipment goes after this stop; on the last leg that is the final delivery.
    const onward = input.isFinalLeg ? `${nextCity || 'you'} for final delivery` : nextOr;

    switch (activity) {
        case 'final': {
            // Last leg starting at a hand-off stop (city): the shipment reaches it, then delivers to nextCity.
            const destination = nextCity || 'you';

            if (phase === 'moving') {
                return {
                    title: { primary: 'Final Delivery Pending', secondary: finalItems ? `${nearing} · ${finalItems} on board` : nearing },
                    description: `Your shipment is on its way to ${cityOr}, then on to ${destination} for final delivery.`,
                };
            }

            if (phase === 'arrived') {
                return {
                    title: { primary: 'Ready for Final Delivery', secondary: finalItems ? `${inCity} · ${finalItems} heading to you` : inCity },
                    description: `Your shipment is in ${city || 'its last stop'} and leaves next for final delivery in ${destination}.`,
                };
            }

            return {
                title: { primary: 'Final Delivery Pending', secondary: finalItems ? `${finalItems} deliver in ${destination}` : `Heading to ${destination}` },
                description: `Your shipment continues through ${cityOr} to ${destination} for final delivery.`,
            };
        }

        case 'pickup': {
            if (phase === 'moving') {
                return {
                    title: { primary: 'Pickup Pending', secondary: pickupItems ? `${nearing} · ${pickupItems} pick up on arrival` : nearing },
                    description: `Your shipment is on its way to ${cityOr}, where ${pickupWhat} will be picked up.`,
                };
            }

            if (phase === 'arrived') {
                return {
                    title: { primary: 'Ready for Pickup', secondary: pickupItems ? `${inCity} · ${pickupItems} picking up` : inCity },
                    description: `Your shipment is in ${cityOr}. ${cap(pickupWhat)} will be picked up here before it continues to ${onward}.`,
                };
            }

            return {
                title: { primary: 'Pickup Pending', secondary: city ? `${pickupItems ? `${pickupItems} pick up` : 'Pickup'} in ${city}` : 'Pickup at the next stop' },
                description: `${cap(pickupWhat)} will be picked up in ${cityOr} once your shipment arrives there.`,
            };
        }

        case 'drop': {
            if (phase === 'moving') {
                return {
                    title: { primary: 'Delivery Pending', secondary: dropItems ? `${nearing} · ${dropItems} delivering` : nearing },
                    description: `Your shipment is on its way to ${cityOr}, where ${dropWhat} will be delivered.`,
                };
            }

            if (phase === 'arrived') {
                return {
                    title: { primary: city ? `Delivering in ${city}` : 'Delivering', secondary: dropItems ? `${dropItems} delivering` : inCity },
                    description: `Your shipment is in ${cityOr}. ${cap(dropWhat)} will be delivered here before the rest continues to ${onward}.`,
                };
            }

            return {
                title: { primary: 'Delivery Pending', secondary: city ? `${dropItems ? `${dropItems} deliver` : 'Delivery'} in ${city}` : 'Delivery at the next stop' },
                description: `${cap(dropWhat)} will be delivered in ${cityOr} once your shipment arrives there.`,
            };
        }

        case 'both': {
            const both = dropItems && pickupItems ? `${dropItems} delivering, ${pickupItems} picking up` : null;

            if (phase === 'moving') {
                return {
                    title: { primary: 'Delivery & Pickup Pending', secondary: both ? `${nearing} · ${both}` : nearing },
                    description: `Your shipment is on its way to ${cityOr}, where ${dropWhat} will be delivered and ${pickupWhat} picked up.`,
                };
            }

            if (phase === 'arrived') {
                return {
                    title: { primary: 'Ready for Delivery & Pickup', secondary: both ? `${inCity} · ${both}` : inCity },
                    description: `Your shipment is in ${cityOr}. ${cap(dropWhat)} will be delivered and ${pickupWhat} picked up here before it continues to ${onward}.`,
                };
            }

            return {
                title: { primary: 'Delivery & Pickup Pending', secondary: city ? `Next up in ${city}` : 'Next up at the next stop' },
                description: `${cap(dropWhat)} will be delivered and ${pickupWhat} picked up in ${cityOr} once your shipment arrives there.`,
            };
        }

        case 'transfer': {
            const description = `Your shipment changes trucks in ${cityOr}, then continues to ${nextOr}.`;

            if (phase === 'moving') return { title: { primary: 'Transfer Pending', secondary: nearing }, description };
            if (phase === 'arrived') return { title: { primary: 'Transfer in Progress', secondary: inCity }, description };

            return { title: { primary: 'Transfer Pending', secondary: headingTo }, description };
        }

        default: {
            if (phase === 'moving') {
                return {
                    title: { primary: 'In Transit', secondary: nearing },
                    description: `Your shipment is on its way to ${cityOr}.`,
                };
            }

            if (phase === 'arrived') {
                return {
                    title: { primary: city ? `Arrived in ${city}` : 'Arrived', secondary: `Continuing to ${nextOr}` },
                    description: `Your shipment reached ${cityOr} and continues to ${nextOr}.`,
                };
            }

            return {
                title: { primary: 'Next Stop', secondary: city || 'Coming up' },
                description: `Your shipment reaches ${cityOr} after its earlier stops.`,
            };
        }
    }
};

/** "4 pallets were delivered" style phrase, exported for the delivered card. */
export const itemsWere = (items: string): string => withItemsVerb(items, 'was', 'were');
